import { useEffect, useState, useRef } from "react";
import { Upload, Trash2, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function MediaManager() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => { setLoading(true); api.get("/admin/media").then(({ data }) => setMedia(data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} image(s) téléversée(s)`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (id) => { if (!window.confirm("Supprimer cette image ?")) return; await api.delete(`/admin/media/${id}`); load(); };
  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("URL copiée"); };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div><h1 className="font-serif text-3xl text-anthracite">Médiathèque</h1><p className="text-sm text-anthracite/50">Téléversez et gérez les images de la plateforme.</p></div>
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" data-testid="media-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload-btn" className="inline-flex items-center gap-2 bg-anthracite px-5 py-2.5 text-sm font-medium text-ivory hover:bg-black disabled:opacity-50">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Téléverser
        </button>
      </div>

      {loading ? <p className="text-anthracite/40">…</p> : media.length === 0 ? (
        <div className="border border-dashed border-tertiary bg-white py-20 text-center text-sm text-anthracite/40" data-testid="media-empty">Aucune image pour l'instant.</div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {media.map((m) => (
            <div key={m.id} data-testid={`media-item-${m.id}`} className="group border border-tertiary bg-white">
              <img src={m.url} alt={m.original_filename} className="aspect-square w-full object-cover" />
              <div className="flex items-center justify-between gap-1 p-2">
                <span className="truncate text-xs text-anthracite/50" title={m.original_filename}>{m.original_filename}</span>
                <div className="flex shrink-0 gap-1">
                  <button onClick={() => copy(m.url)} className="rounded p-1 text-anthracite/50 hover:text-champagne" aria-label="Copier"><Copy className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(m.id)} className="rounded p-1 text-anthracite/50 hover:text-red-600" aria-label="Supprimer"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
